import React, { Dispatch, SetStateAction } from "react"
import {
  DateWrapper,
  InputWrapper,
  Name,
  SelectWrapper,
  StyledButton
} from "../CampaignInput.styled"
import { Controller, UseFormGetValues } from "react-hook-form"
import Input from "../../Ui/Form/Input"
import SingleSelect from "../../Ui/Form/SingleSelect"
import { Control, UseFormSetValue } from "react-hook-form/dist/types/form"
import { CampaignGroup, InputCampaign } from "lib/types/generated/graphql-types"
import { FieldErrors } from "react-hook-form/dist/types/errors"
import { Option } from "lib/types/common"
import { Loader } from "../../Ui/Loader"
import dayjs from "dayjs"
import { CreateCampaignGroup } from "../../../views/Campaigns/CreateCampaignGroup"
import { showEditSidebar } from "lib/store/services/editSidebar/slice"
import { useDispatch } from "react-redux"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"
import FallbackInput from "../../Ui/Form/FallbackInput"

type DefaultValues = {
  name?: string
  startDate?: string
  endDate?: string
  campaignGroupId: string
}

type Props = {
  control: Control<InputCampaign>
  errors: FieldErrors<InputCampaign>
  setValue: UseFormSetValue<InputCampaign>
  getValues: UseFormGetValues<InputCampaign>
  campaignGroups: CampaignGroup[]
  defaultValues?: DefaultValues
  setCampaignGroups: Dispatch<SetStateAction<CampaignGroup[]>>
  isCampaignOngoing?: boolean
  startDateValidation: boolean
}

const General = ({
  control,
  errors,
  setValue,
  getValues,
  campaignGroups,
  defaultValues,
  setCampaignGroups,
  isCampaignOngoing,
  startDateValidation
}: Props) => {
  const dispatch = useDispatch()

  const campaignGroupOptions = campaignGroups?.map((campaignGroup: CampaignGroup) => ({
    value: campaignGroup.id,
    label: campaignGroup.name
  })) as Option[]
  return (
    <>
      <h2>General</h2>
      <InputWrapper>
        <Name>
          <WithAuth
            requiredGroups={PermissionGroups.campaigns.write}
            fallback={<FallbackInput label={"Name"} value={defaultValues?.name} />}
          >
            <Controller
              name="name"
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  label="Name *"
                  placeholder="Aa - Zz, 0 - 9"
                  errors={errors}
                  disabled={isCampaignOngoing}
                />
              )}
              defaultValue={defaultValues?.name ?? ""}
              control={control}
              rules={{
                required: "This is a required field"
              }}
            />
          </WithAuth>
        </Name>
        <SelectWrapper>
          {!campaignGroups ? (
            <Loader color={"black"} />
          ) : (
            <WithAuth
              requiredGroups={PermissionGroups.campaigns.write}
              fallback={
                <FallbackInput
                  label={"Campaign group"}
                  value={
                    campaignGroups?.find((group) => group.id === defaultValues?.campaignGroupId)
                      ?.name ?? defaultValues?.campaignGroupId
                  }
                />
              }
            >
              <Controller
                name="campaignGroupId"
                render={({ field }) => (
                  <SingleSelect
                    {...field}
                    label="Campaign group *"
                    options={campaignGroupOptions}
                    disabled={isCampaignOngoing}
                    defaultValue={
                      defaultValues?.campaignGroupId
                        ? {
                            label:
                              campaignGroups?.find(
                                (group) => group.id === defaultValues?.campaignGroupId
                              )?.name ?? defaultValues?.campaignGroupId,
                            value: defaultValues?.campaignGroupId
                          }
                        : {
                            label:
                              campaignGroups?.find((group) => group.id === field.value)?.name ??
                              field.value,
                            value: field.value
                          }
                    }
                    setValue={(e) => setValue("campaignGroupId", e?.value)}
                  />
                )}
                control={control}
                rules={{
                  required: "Campaign group is required."
                }}
              />
            </WithAuth>
          )}
        </SelectWrapper>
        <WithAuth requiredGroups={PermissionGroups.campaigns.write}>
          <StyledButton handleClick={() => dispatch(showEditSidebar())}>+ Group</StyledButton>
        </WithAuth>
        <CreateCampaignGroup setCampaignGroups={setCampaignGroups} />
      </InputWrapper>
      <DateWrapper>
        <WithAuth
          requiredGroups={PermissionGroups.campaigns.write}
          fallback={
            <FallbackInput
              label={"Time Period"}
              dateTime={{from: defaultValues?.startDate ?? "", to: defaultValues?.endDate ?? ""}}
              value={dayjs(defaultValues?.startDate).format("YYYY-MM-DD HH:mm")}
            />
          }
        >
          <Controller
            name="startDate"
            render={({ field }) => (
              <Input
                {...field}
                type="datetime-local"
                label="Start date*"
                placeholder="Date"
                disabled={isCampaignOngoing}
                min={dayjs().format("YYYY-MM-DD HH:mm")}
                errors={errors}
              />
            )}
            defaultValue={
              defaultValues?.startDate
                ? dayjs(defaultValues?.startDate).format("YYYY-MM-DD HH:mm")
                : dayjs().add(1, "hour").format("YYYY-MM-DD HH:mm")
            }
            control={control}
            rules={{
              required: "Start date is required",
              ...(startDateValidation && {
                min: {
                  value: dayjs().format("YYYY-MM-DD HH:mm"),
                  message: `Can't be a date before ${dayjs().format("YYYY-MM-DD HH:mm")}`
                }
              })
            }}
          />
        </WithAuth>
        <WithAuth
          requiredGroups={PermissionGroups.campaigns.write}
        >
          <Controller
            name="endDate"
            render={({ field }) => (
              <Input
                {...field}
                type="datetime-local"
                label="End date*"
                placeholder="Date"
                min={getValues("startDate")}
                errors={errors}
              />
            )}
            defaultValue={
              defaultValues?.endDate ? dayjs(defaultValues?.endDate).format("YYYY-MM-DD HH:mm") : ""
            }
            control={control}
            rules={{
              required: "End date is required",
              min: {
                value: dayjs(getValues("startDate")).format("YYYY-MM-DD HH:mm"),
                message: `Can't be a date before start date`
              }
            }}
          />
        </WithAuth>
      </DateWrapper>
    </>
  )
}

export default General
